import { useForm, FormProvider } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useStore } from "../../../../../helpers";
import type { SourceFile } from "../sources-types";
import { FTPInput } from "../../../../../components-new";
import { formSubtitleClass } from "./addSourceStyles";
import { useSourceForm } from "./useSourceForm";
import { SourceFormFooter } from "./SourceFormFooter";
import { fstTemplateLabel } from "./connectDataBankLabels";
import { resolveDataBankViewType } from "../../analytics/dataBankSourceHelpers";
import {
    DATA_BANK_VIEW_CONSTITUENCY_MAP,
    DATA_BANK_VIEW_PAST_ELECTION,
} from "./dataBankViewTypes";
import { connectDataBankMorePayload, isConnectDataBankFormComplete } from "./connectDataBankSubmit";
import { ConnectDataBankPickers, type ConnectDataBankFormVals } from "./ConnectDataBankPickers";

const TITLE = "Connect Data Bank";

export default function ConnectDataBankForm({
    parentId,
    editNode,
    onSuccess,
    onCancel,
}: {
    parentId: string | null;
    editNode?: SourceFile | null;
    onSuccess: () => void;
    onCancel: () => void;
}) {
    const { flexiStore } = useStore();
    const { isEdit, submitSource, renameNode } = useSourceForm(parentId, editNode ?? null, onSuccess);
    const editViewType = editNode ? resolveDataBankViewType(editNode) : "";

    const methods = useForm<ConnectDataBankFormVals>({
        defaultValues: {
            name: editNode?.name ?? "",
            fst: null,
            fsd: null,
            viewType: editViewType ?? "",
            election: editNode?.data_bank_election ?? "",
            mapKey: editNode?.data_bank_map_key ?? "",
        },
    });
    const fst = methods.watch("fst");
    const fsd = methods.watch("fsd");
    const viewType = methods.watch("viewType");

    const { data: fstList = [], isLoading: fstLoading } = useQuery({
        queryKey: ["tars-connect-databank", "fst"],
        queryFn: () => flexiStore.fetchFstTemplatesForDataBankConnect(""),
    });
    const { data: fsdList = [], isLoading: fsdLoading } = useQuery({
        queryKey: ["tars-connect-databank", "fsd", fst?.key],
        queryFn: () => flexiStore.fetchFsdRowsForDataBankConnect(fst!.key),
        enabled: !!fst?.key,
    });
    const { data: elections = [], isLoading: elLoading } = useQuery({
        queryKey: ["tars-connect-databank", "elections", fsd?.id],
        queryFn: async () => {
            try {
                return await flexiStore.fetchPastElectionKeysForDataBankConnect(fsd!.id);
            } catch {
                return [];
            }
        },
        retry: false,
        enabled: fsd != null && fsd.id > 0 && viewType === DATA_BANK_VIEW_PAST_ELECTION,
    });
    const { data: mapKeys = [], isLoading: mapLoading } = useQuery({
        queryKey: ["tars-connect-databank", "maps", fsd?.id],
        queryFn: async () => {
            try {
                return await flexiStore.fetchConstituencyMapKeysForDataBankConnect(fsd!.id);
            } catch {
                return [];
            }
        },
        retry: false,
        enabled: fsd != null && fsd.id > 0 && viewType === DATA_BANK_VIEW_CONSTITUENCY_MAP,
    });

    const onSubmit = async (data: ConnectDataBankFormVals) => {
        const name = data.name?.trim() || (data.fst ? fstTemplateLabel(data.fst) : "") || TITLE;
        if (isEdit) {
            await renameNode(name);
            return;
        }
        if (!isConnectDataBankFormComplete(data)) return;
        await submitSource({
            type: "data_bank",
            name,
            more: connectDataBankMorePayload(data),
        });
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <h3 className={formSubtitleClass}>{TITLE}</h3>
                <FTPInput name="name" label="Name" className="mb-3" size="small" placeholder="Source label" />
                <ConnectDataBankPickers
                    control={methods.control}
                    setValue={methods.setValue}
                    watch={methods.watch}
                    fstList={fstList}
                    fsdList={fsdList}
                    elections={elections}
                    mapKeys={mapKeys}
                    fstLoading={fstLoading}
                    fsdLoading={fsdLoading}
                    elLoading={elLoading}
                    mapLoading={mapLoading}
                    isEdit={isEdit}
                />
                <SourceFormFooter
                    onCancel={onCancel}
                    submitLabel={isEdit ? "Save" : "Add"}
                    disabled={!isEdit && !isConnectDataBankFormComplete(methods.watch())}
                />
            </form>
        </FormProvider>
    );
}
